// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

/*
 * The words.
 *
 * This tool is used by one person to look at a document another person gave
 * them, very often in a housing or employment decision. That makes the wording
 * a safety feature rather than a matter of taste, and two rules govern all of
 * it.
 *
 * The first is that peelback describes and never concludes. There is no score
 * here, no verdict, and no word for "suspicious" — tests/language.test.ts
 * enforces that over this file, so the ban cannot quietly regress.
 *
 * The second is the base rate, which is the fact everything else hangs off.
 * Six official PDFs published by the IRS, HUD and the US Government Publishing
 * Office were measured while this was designed. Five of the six carry more than
 * one end-of-file marker — the thing every article tells you to count — and in
 * each of those five at least one marker is not an earlier version at all. Where
 * a real earlier version WAS there, a digital signature had put it there. So
 * both halves of the naive reading are wrong, and a tool that presented either
 * as a warning would be telling landlords something alarming about the IRS's own
 * W-4 form, and some of them would believe it.
 *
 * Note the precision: the measured quantity is MARKERS, not saves. Restating it
 * as a save count would be the same overcount this whole tool exists to correct,
 * which is why tests/language.test.ts asserts the exported wording.
 */

import { term } from './glossary.ts';

export const HOW_IT_WORKS = `
  <p>
    A PDF does not get rewritten when you save it again. The changed parts are added to the end and
    everything already in the file stays exactly where it was. That is in the format's specification, and
    it is the whole reason an earlier version of a document can still be sitting inside the copy in your
    inbox.
  </p>
  <ol class="steps">
    <li>
      <strong>Start at the end and walk backwards.</strong> The last thing in a PDF is a pointer to its
      ${term('xref', 'index')}. That index names the one before it, which names the one before that.
      Following that chain is the only way to see the real history — and it is the reason peelback
      never searches the file for ${term('eof', '%%EOF markers')}, which is what most advice on the
      internet tells you to do.
    </li>
    <li>
      <strong>Read every index, including the compressed ones.</strong> Files written this decade keep
      their index as a compressed stream and pack their contents into an ${term('objstm', 'object stream')}.
      The words <code>xref</code> and <code>trailer</code> are not in such a file anywhere. Anything
      searching for them reports one save no matter how many there were.
    </li>
    <li>
      <strong>Cut the file at each point on the chain, and check what falls out.</strong> The bytes from
      the beginning to the end of save three <em>are</em> the document as it stood at save three. Each
      one is then re-read from scratch by a fresh, strict parser, and opened again by a completely
      separate PDF engine. Anything that fails either check is reported as unreadable rather than
      handed to you.
    </li>
    <li>
      <strong>Read the labels the software left behind.</strong> ${term('producer', 'Producer and Creator')},
      the dates, any ${term('xmp', 'XMP')} history, and any digital signature's
      ${term('bytrange', 'covered range')}. Each of these is shown with what it ordinarily means, because
      that half is usually missing and it is the half that stops a reader jumping.
    </li>
    <li>
      <strong>Look at the pages yourself.</strong> Any two saves can be put side by side and read. That
      is the part worth having. A finding about metadata is something you have to take on trust; two
      renderings of page two with a different number on each is something you can see.
    </li>
  </ol>
  <h3>Traps this avoids, and what they cost</h3>
  <p>
    Three ordinary things put extra <code>%%EOF</code> markers in a file, and each one makes a
    marker-counting tool announce an earlier version of a document that was saved exactly once.
    ${term('linearized', 'Web optimisation')} puts an index near the start of the file. An attached
    PDF brings its own markers, its own index and its own signature along with it. And a page that
    prints the characters — this one does, on purpose — adds another. Try the three samples labelled
    <strong>Trap</strong>: each contains extra markers, and peelback reports one save for all three.
  </p>
`;

export const WHAT_IT_CANNOT_TELL_YOU = `
  <p>
    peelback reports what is in a file's bytes. It cannot tell you whether a document is genuine, and
    it is not built to.
  </p>
  <h3>One save means nothing</h3>
  <p>
    Anyone changing a document can leave no trace at all. Printing it, exporting it, choosing “Save As”
    instead of “Save”, or passing it through almost any free online PDF tool
    ${term('flatten', 'flattens')} it into a brand-new file with no history in it. A document with one
    save is the normal state of a document that was never touched <em>and</em> the normal state of one
    that was carefully changed.
  </p>
  <h3>Many saves means nothing either</h3>
  <p>
    Signing a document is a save. Filling in a form field is a save. Adding a comment is a save. A
    reader adding validation data to a signature, by itself, when the file is opened, is a save. Of six
    PDFs published by the IRS, HUD and the US Government Publishing Office that were measured while this
    tool was built, five carry more than one end-of-file marker. Most of those extra markers are web
    optimisation and are not saves at all — and where a real earlier version was there, it was a digital
    signature that had put it there rather than anybody editing the document. The more official a
    document is, the more likely it is to have been signed and therefore saved again.
  </p>
  <h3>The labels are self-reported</h3>
  <p>
    ${term('producer', 'Producer')} and ${term('producer', 'Creator')} are free text an application
    writes about itself. They can be edited, they are replaced by the last thing to save the file, and
    the innocent explanations are usually the right ones. A statement printed from a bank's website
    says Chrome, because it was printed from a bank's website. The US Government Publishing Office
    publishes the Code of Federal Regulations with the same general-purpose library somebody
    fabricating a payslip would reach for.
  </p>
  <h3>What actually settles it</h3>
  <p>
    Ask the issuer. A payroll department can confirm a payslip; a bank can send a statement directly,
    or share it through open banking. A document's bytes cannot tell you whether the numbers in it are
    true — only the organisation that produced it can. If something here concerns you, that is the next
    step, and it is the only one this tool recommends.
  </p>
`;

export const BEFORE_YOU_ACT = `
  <p>
    If you are reading this because you are deciding whether to give someone a home or a job, please
    read this part.
  </p>
  <h3>Nothing here is evidence of wrongdoing</h3>
  <p>
    Everything peelback shows you has ordinary explanations, and on the documents most likely to be
    real — signed, official, issued by a large organisation — the findings fire hardest. Treating them
    as a reason to say no would mean turning people away over web optimisation and digital signatures.
  </p>
  <h3>Ask, do not accuse</h3>
  <p>
    If something does not add up, go to the source: the employer's payroll department, or the bank, or
    ask the person to send the document to you directly from the issuer's own app. Do not confront
    anybody with a report about file structure. Do not forward it to a letting agent, an employer or
    the police as though it settled something.
  </p>
  <h3>Turning someone down on this can create legal exposure</h3>
  <p>
    In the United States a tenant-screening practice can be unlawful because of its effect even without
    any discriminatory intent — that is the discriminatory-effect standard at 24 CFR §100.500(a), and
    a screen that flags most ordinary documents is exactly the kind a court is asked to look at. In the
    United Kingdom a landlord is a data controller in their own right, with data-minimisation duties
    and Equality Act exposure. Neither is legal advice; both are reasons to talk to the issuer instead.
  </p>
  <h3>Tell the person what you saw</h3>
  <p>
    A landlord or employer looking at a document somebody handed them directly is not a credit-reporting
    agency, so none of the machinery that normally gives a person the right to see and dispute a
    decision applies. They will not be told, and they cannot correct it. If you rely on anything here,
    show them — peelback can export a plain-language copy written for them, so they can answer it.
  </p>
`;

export const PRIVACY = `
  <p>
    The files this tool is for are payslips, bank statements, tenancy applications and offer letters.
    They carry salaries, addresses, account numbers and other people's names. They are read here, in
    this tab, and they are not sent anywhere.
  </p>

  <h3>Protected</h3>
  <ul class="trust-list trust-yes">
    <li>Your PDF is read in this browser tab. It is never uploaded, and there is no server to upload it to — this is a static page.</li>
    <li>Recovered earlier versions are built in memory. They exist only while the tab is open and are gone when you close it.</li>
    <li>Nothing is written to disk. No cookies, no local storage, no database, no cache of anything you opened.</li>
    <li>The report, the CSV and every recovered version are produced on your machine and saved by your own browser.</li>
    <li>After the first visit the tool works with no network at all. Turn off your connection and it still runs.</li>
    <li>No third-party fonts, no trackers, no advertising, and no “document authenticity” service being asked about your file.</li>
  </ul>

  <h3>Not protected</h3>
  <ul class="trust-list trust-no">
    <li>
      <strong>Recovering an earlier version can undo a redaction.</strong> The commonest way people
      remove information before sharing a document is a black rectangle drawn over it and then saved —
      which is a save, so the text underneath is still in the file and peelback can bring it back. On a
      bank statement that can mean an account number, or the name of a therapist, a solicitor or a
      union. Those people did not agree to share it with you.
    </li>
    <li>Anything you download is an ordinary file on your computer, with all the same exposure as any other. If you recovered something sensitive, delete it.</li>
    <li>Anyone with access to this device can see what is on the screen. Nothing here is protected from someone standing behind you.</li>
    <li>peelback cannot tell whether a document is genuine, and no reading of a file's bytes can.</li>
  </ul>

  <h3>Who can see anything</h3>
  <ul class="trust-list trust-eye">
    <li>The page itself, served by GitHub Pages over TLS. GitHub can see that you loaded the page. It cannot see your file.</li>
    <li>A Cloudflare Web Analytics beacon records anonymous page views — no cookies, no fingerprinting, no cross-site tracking; your files and data are never sent to it.</li>
    <li>Feedback you choose to send is sent to feedback.benrichardson.dev. Nothing is sent unless you open the feedback form and press Send; your files and data never are.</li>
    <li>That is the complete list. The page's content-security policy names those three origins and nothing else, and the build fails if anything is added to it.</li>
  </ul>

  <h3>Why there is no server, on purpose</h3>
  <p>
    A version of this that uploaded your file would be able to do more. It would also mean a company
    holding a copy of a named person's un-redacted bank statement, plus a record that you ran a check
    on them — and in the United States it would start to look like the kind of service that has legal
    duties about accuracy and disclosure. Keeping everything in the tab is not only a privacy
    preference. It is what keeps this a tool you use rather than a file about somebody.
  </p>
`;

export const ABOUT = `
  <p>
    <strong>peelback</strong> reads a PDF's production chain and recovers the earlier versions still
    buried inside it. Everything happens in your browser.
  </p>
  <p>
    It was built because the advice people find when they search for this is to open the file in a text
    editor and count <code>%%EOF</code> markers — and that advice is wrong often enough to be harmful.
    Six official PDFs published by the IRS, HUD and the US Government Publishing Office were measured
    while this was designed: five of the six carry more than one marker, and in every one of those five
    at least one marker is not an earlier version of the document. peelback follows the cross-reference
    chain instead, which is the only reading that can be checked.
  </p>
  <p>
    The PDF reader here is written from scratch — the cross-reference tables and streams, the PNG
    predictor un-filtering, the object streams, the XMP reader, the signature range arithmetic. It has
    to be: the excellent libraries all repair a broken index automatically, and a reader that repairs
    the thing it is meant to be testing cannot test it. A separate engine, pdf.js, is used afterwards
    as an independent second opinion and to draw the pages.
  </p>
  <p>
    Built by <a href="https://benrichardson.dev/" target="_blank" rel="noopener">Ben Richardson</a>.
    More tools and sites at <a href="https://lab.benrichardson.dev" target="_blank" rel="noopener">lab.benrichardson.dev</a>.
    The source is at <a href="https://github.com/ben-gy/peelback" target="_blank" rel="noopener">github.com/ben-gy/peelback</a>.
  </p>
  <p>
    If peelback reads a file wrongly — a version it invents, a version it misses, or a producer it does
    not recognise — that is the most useful thing you can report. Use the feedback link at the bottom of
    the page, and describe how the file was made rather than sending it.
  </p>
`;

export const UNREDACTION_WARNING = `
  <p>
    Before this is recovered, one thing worth knowing.
  </p>
  <p>
    An earlier version of a document can contain information the sender deliberately took out. The
    usual way people remove something before sharing a file is to draw a black rectangle over it and
    save — and saving is what leaves the earlier version behind. So the version you are about to open
    may show what was under the rectangle.
  </p>
  <p>
    On a bank statement that can be a full account number, and the names of people they pay — a doctor,
    a solicitor, a union, a place of worship, someone they support. Those people are not party to
    whatever you are checking and did not agree to any of this.
  </p>
  <p>
    Recovering it may be more than you need. If what you are trying to establish is whether a figure is
    right, ask the organisation that issued the document — that answers the question properly, and this
    does not.
  </p>
`;
